import { type StudyThreadSelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { Skeleton } from '@components/ui/skeleton';
import { EditIcon } from 'lucide-react';
import { type FC, useEffect, useState } from 'react';
import { MeiaThreadModal } from './meia-thread-modal.component';
import { MeiaThreadPickerCard, MeiaThreadPickerTileTitle } from './meia-thread-picker-card.component';

type MeiaThreadManagerProps = {
  canEdit: boolean;
  isLoading?: boolean;
  missionId: string;
  onCreateThreadActionConsumed?: () => void;
  onSelectThread: (threadId: string) => void;
  selectedThreadId?: string;
  shouldOpenCreateThread?: boolean;
  studyId: string;
  threads: StudyThreadSelectDto[];
};

export const MeiaThreadManager: FC<MeiaThreadManagerProps> = (props) => {
  const {
    canEdit,
    isLoading = false,
    missionId,
    onCreateThreadActionConsumed,
    onSelectThread,
    selectedThreadId,
    shouldOpenCreateThread = false,
    studyId,
    threads,
  } = props;
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const editingThread = threads.find((thread) => thread.id === editingThreadId);

  useEffect(() => {
    if (!canEdit || !shouldOpenCreateThread) return;
    setIsCreating(true);
    onCreateThreadActionConsumed?.();
  }, [canEdit, onCreateThreadActionConsumed, shouldOpenCreateThread]);

  const headerButton = canEdit ? { onClick: () => setIsCreating(true), text: '+ Add Thread' } : undefined;

  return (
    <>
      <Tile
        contentClassName="gap-3"
        emptyState={{
          action: canEdit ? (
            <Button
              onClick={() => setIsCreating(true)}
              variant="link"
            >
              Add Mission Thread
            </Button>
          ) : undefined,
          title: 'No Mission Threads',
        }}
        header={{ button: headerButton, title: <MeiaThreadPickerTileTitle /> }}
        isEmpty={!isLoading && threads.length === 0}
      >
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <div className="flex flex-wrap gap-2">
            {threads.map((thread) => (
              <MeiaThreadPickerCard
                editButton={
                  canEdit ? (
                    <IconButton
                      ariaLabel={`Edit thread ${thread.name}`}
                      icon={<EditIcon size={14} />}
                      onClick={() => setEditingThreadId(thread.id)}
                      size="sm"
                      variant="secondary"
                    />
                  ) : undefined
                }
                isSelected={thread.id === selectedThreadId}
                key={thread.id}
                onSelect={() => onSelectThread(thread.id)}
                studyId={studyId}
                thread={thread}
              />
            ))}
          </div>
        )}
      </Tile>

      {(isCreating || editingThread) && (
        <MeiaThreadModal
          isOpen={isCreating || Boolean(editingThread)}
          missionId={missionId}
          onClose={() => {
            setEditingThreadId(null);
            setIsCreating(false);
          }}
          studyId={studyId}
          thread={editingThread}
        />
      )}
    </>
  );
};
